import axios from 'axios';
import { buildApiUrl } from './apiConfig';

const PUBLIC_BASE_URL = 'https://qrmaps.me/f';

const sharedFilesUrl = (path = '') => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return buildApiUrl(`shared-files/${cleanPath}`);
};

const resolveAuthHeaders = async (getApiHeaders) => {
  if (typeof getApiHeaders !== 'function') {
    return {
      ok: false,
      error: 'Authentication is not configured for file sharing.'
    };
  }

  const headers = await getApiHeaders();
  if (!headers?.Authorization) {
    return {
      ok: false,
      error: 'Please sign in to manage shared files.'
    };
  }

  return {
    ok: true,
    headers
  };
};

/**
 * Upload a file to the sharing service
 * @param {File} file - The file to upload
 * @param {function} getApiHeaders - Function to get auth headers
 * @param {number|null} expirationDays - Number of days until expiration (1-365) or null for no expiration
 * @param {string} description - Optional description
 * @param {function} onProgress - Callback for upload progress (0-100)
 * @returns {Promise<Object>} Upload response with code and public_url
 */
export const uploadFile = async (file, getApiHeaders, expirationDays = null, description = '', onProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('original_filename', file.name);

  if (expirationDays !== null && expirationDays !== '') {
    formData.append('expires_days', expirationDays);
  }

  if (description) {
    formData.append('description', description);
  }

  try {
    const auth = await resolveAuthHeaders(getApiHeaders);
    if (!auth.ok) {
      return {
        success: false,
        error: auth.error
      };
    }

    const response = await axios({
      url: sharedFilesUrl('upload/'),
      method: 'post',
      data: formData,
      headers: {
        ...auth.headers,
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      }
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Upload error:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Upload failed'
    };
  }
};

/**
 * Get list of user's uploaded files
 * @param {function} getApiHeaders - Function to get auth headers
 * @param {number} page - Page number (default 1)
 * @param {number} pageSize - Number of items per page (default 20)
 * @returns {Promise<Object>} List of files with pagination
 */
export const listFiles = async (getApiHeaders, page = 1, pageSize = 20) => {
  try {
    const auth = await resolveAuthHeaders(getApiHeaders);
    if (!auth.ok) {
      return {
        success: false,
        error: auth.error
      };
    }

    const response = await axios({
      url: sharedFilesUrl(''),
      method: 'get',
      params: {
        page,
        page_size: pageSize
      },
      headers: auth.headers
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('List files error:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to load files'
    };
  }
};

/**
 * Get details of a specific file
 * @param {function} getApiHeaders - Function to get auth headers
 * @param {number} fileId - File ID
 * @returns {Promise<Object>} File details
 */
export const getFileDetails = async (getApiHeaders, fileId) => {
  try {
    const auth = await resolveAuthHeaders(getApiHeaders);
    if (!auth.ok) {
      return {
        success: false,
        error: auth.error
      };
    }

    const response = await axios({
      url: sharedFilesUrl(`${fileId}/`),
      method: 'get',
      headers: auth.headers
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Get file details error:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to load file details'
    };
  }
};

/**
 * Delete a file
 * @param {function} getApiHeaders - Function to get auth headers
 * @param {number} fileId - File ID to delete
 * @returns {Promise<Object>} Delete response
 */
export const deleteFile = async (getApiHeaders, fileId) => {
  try {
    const auth = await resolveAuthHeaders(getApiHeaders);
    if (!auth.ok) {
      return {
        success: false,
        error: auth.error
      };
    }

    const response = await axios({
      url: sharedFilesUrl(`${fileId}/`),
      method: 'delete',
      headers: auth.headers
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Delete file error:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to delete file'
    };
  }
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Get file icon emoji based on content type
 * @param {string} contentType - MIME type
 * @returns {string} Emoji icon
 */
export const getFileIcon = (contentType) => {
  const icons = {
    'application/pdf': '📄',
    'image/jpeg': '🖼️',
    'image/png': '🖼️',
    'image/gif': '🖼️',
    'video/mp4': '🎥',
    'video/quicktime': '🎥',
    'application/zip': '📦',
    'application/x-zip-compressed': '📦',
    'text/plain': '📝',
    'application/msword': '📃',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📃',
    'application/vnd.ms-excel': '📊',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📊',
  };

  return icons[contentType] || '📎';
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (err) {
      document.body.removeChild(textArea);
      console.error('Failed to copy:', err);
      return false;
    }
  }
};

/**
 * Format date for display in dd/mm/yy format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffTime = date - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Expired';

  // Format as dd/mm/yy
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};

/**
 * Validate file size
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum size in MB (default 100)
 * @returns {Object} Validation result
 */
export const validateFileSize = (file, maxSizeMB = 100) => {
  const maxBytes = maxSizeMB * 1024 * 1024;

  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `File size exceeds ${maxSizeMB}MB limit. Selected file is ${formatFileSize(file.size)}.`
    };
  }

  return { valid: true };
};

/**
 * Generate public URL for a file code
 * @param {string} code - 9-character file code
 * @returns {string} Public URL
 */
export const getPublicUrl = (code) => {
  return `${PUBLIC_BASE_URL}/${code}`;
};
